import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo, { buildBreadcrumbSchema } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Renders one of the 50 SEO landing pages driven by /api/seo/landing/:slug.
 * Mounted at /s/:slug (so /s/weddings, /s/invites, /s/rsvp, etc.).
 */
export default function SeoLandingPage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/seo/landing/${slug}`)
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setErr(e?.response?.status === 404 ? "not_found" : "error"));
    return () => {
      alive = false;
    };
  }, [slug]);

  if (err === "not_found") {
    return (
      <div className="min-h-screen bg-[var(--bg)] text-[var(--ink)]">
        <Navbar />
        <div className="container-wide section-pad text-center">
          <p className="font-script text-3xl text-[var(--gold)]">Hmm.</p>
          <h1 className="font-serif text-5xl mt-4">We couldn't find that page.</h1>
          <button onClick={() => nav("/")} className="btn-gold mt-8" data-testid="seo-404-home">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[var(--bg)] text-[var(--ink)]">
        <Navbar />
        <div className="container-wide section-pad text-center text-sm text-black/40">Loading…</div>
      </div>
    );
  }

  const { h1, h2 = [], h3 = [], tag, primary_keyword, keywords, cta_label, cta_route, schema_faq, og_image } = data;
  const ogUrl = og_image ? `${process.env.REACT_APP_BACKEND_URL}${og_image}` : undefined;

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--ink)]">
      <Seo
        title={h1}
        description={tag}
        path={`/s/${slug}`}
        image={ogUrl}
        keywords={keywords}
        schema={[
          schema_faq,
          buildBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: h1, url: `/s/${slug}` },
          ]),
        ]}
      />
      <Navbar />

      <section className="container-wide section-pad" data-testid="seo-landing-hero">
        <p className="font-script text-3xl text-[var(--gold)] mb-3">{primary_keyword}</p>
        <h1 className="font-serif text-5xl md:text-7xl font-light leading-tight max-w-4xl">
          {h1}
        </h1>
        <p className="font-body text-lg md:text-xl text-black/60 max-w-2xl mt-6">{tag}</p>
        <Link to={cta_route} className="btn-gold mt-10 inline-block" data-testid="seo-landing-cta">
          {cta_label}
        </Link>
      </section>

      <section className="container-wide pb-20 grid md:grid-cols-3 gap-10">
        {h2.map((heading, i) => (
          <div
            key={heading}
            className="border border-black/10 rounded-2xl p-8 bg-white/40 backdrop-blur"
            data-testid={`seo-landing-h2-${i}`}
          >
            <h2 className="font-serif text-2xl mb-3">{heading}</h2>
            <p className="text-sm text-black/60">{h3[i] || "Beautiful by default."}</p>
          </div>
        ))}
      </section>

      <section className="container-wide pb-24" data-testid="seo-landing-faq">
        <h2 className="font-serif text-4xl mb-8">Frequently asked</h2>
        <div className="space-y-6 max-w-3xl">
          {(schema_faq?.mainEntity || []).map((q, i) => (
            <div key={i} className="border-b border-black/10 pb-4">
              <h3 className="font-serif text-xl">{q.name}</h3>
              <p className="text-sm text-black/60 mt-2">{q.acceptedAnswer.text}</p>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
